import { FormConfig } from 'components/common/form/FormConfig';
import { BeaconType } from 'core/advertiser/BeaconConnection';
import { useCallback, useMemo } from 'react';
import i18n from 'i18n';
import { validateEmpty } from 'utils/ValidateUtils';
import { TenMaxBeaconMessageType } from 'core/advertiser/TenmaxBeacon';
import styles from './outdoorFields.module.scss';
import { useFormikContext } from 'formik';
import { defaultTo, isEmpty, size } from 'lodash';
import { getByteLength } from 'utils/StringUtil';

const textMessageTypeOptions = [
  {
    value: TenMaxBeaconMessageType.TEXT,
    label: i18n.t('outdoorTenMaxBeaconMessageField.labels.messageTypes.text')
  }
];

export const MAX_TITLE_LENGTH = 50;
export const MAX_MESSAGE_LENGTH = 150;

export const useTenMaxBeaconMessageFields = (beaconType: BeaconType) => {
  const { values, setFieldError } = useFormikContext<{
    tenMaxBeaconMessage?: {
      messageType: TenMaxBeaconMessageType;
      title: string;
      message: string;
      dataIOS?: string;
      dataAndroid?: string;
      bundleIds?: string[];
    };
  }>();

  const { tenMaxBeaconMessage } = values;
  const { dataIOS, dataAndroid } = tenMaxBeaconMessage || {};

  const validateMessage = useCallback((value: string) => {
    const error: string | undefined = validateEmpty(value);
    if (error) {
      return error;
    }
    const messageWithoutLineBreak = defaultTo(value, '').replace(
      /(\r\n|\n|\r)/gm,
      ''
    );
    if (getByteLength(messageWithoutLineBreak) > MAX_MESSAGE_LENGTH) {
      return i18n.t('outdoorTenMaxBeaconMessageField.hints.inputLength', {
        length: MAX_MESSAGE_LENGTH
      });
    }
  }, []);

  const validateTitle = useCallback((value: string) => {
    const error: string | undefined = validateEmpty(value);
    if (error) {
      return error;
    }
    if (getByteLength(value) > MAX_TITLE_LENGTH) {
      return i18n.t('outdoorTenMaxBeaconMessageField.hints.inputLength', {
        length: MAX_TITLE_LENGTH
      });
    }
  }, []);

  const validateBundleIds = useCallback((value: string[]) => {
    let error: string | undefined = validateEmpty(value);
    if (error) {
      return error;
    }
    const uniqueIds = Array.from(new Set(value));
    if (uniqueIds.length !== size(value)) {
      return i18n.t('outdoorTenMaxBeaconMessageField.errors.bundleIdsUnique');
    }
  }, []);

  const validateData = useCallback((dataIOS?: string, dataAndroid?: string) => {
    let error: string | undefined = undefined;
    if (isEmpty(dataIOS) && isEmpty(dataAndroid)) {
      error = i18n.t('outdoorTenMaxBeaconMessageField.errors.data');
    }
    return error;
  }, []);

  // 增加 onChange 是因為當 formik validate 觸發時，
  // field validation function 的另一個平台的 data 當下只能拿到舊值，導致沒辦法正確判斷是否兩者都為空
  // 但 onChange 會是下一個 render 才觸發的 function，當下拿到的兩個 data 都是最新的
  const onDataChange = useCallback(
    (dataIOS?: string, dataAndroid?: string) => {
      let error: string | undefined = validateData(dataIOS, dataAndroid);
      setFieldError('tenMaxBeaconMessage.dataIOS', error);
      setFieldError('tenMaxBeaconMessage.dataAndroid', error);
    },
    [validateData, setFieldError]
  );

  const fields = useMemo(() => {
    if (beaconType !== BeaconType.TENMAX_BEACON) {
      return [];
    }
    return new FormConfig.FieldsBuilder()
      .addFormikSelect({
        label: i18n.t('outdoorTenMaxBeaconMessageField.labels.messageType'),
        name: 'tenMaxBeaconMessage.type',
        simpleValue: true,
        options: textMessageTypeOptions
      })
      .addFormikTags({
        label: i18n.t('outdoorTenMaxBeaconMessageField.labels.bundleIds'),
        name: 'tenMaxBeaconMessage.bundleIds',
        placeholder: i18n.t(
          'outdoorTenMaxBeaconMessageField.placeholders.bundleIds'
        ),
        validate: validateBundleIds
      })
      .addFormikInput({
        label: i18n.t('outdoorTenMaxBeaconMessageField.labels.dataIOS'),
        name: 'tenMaxBeaconMessage.dataIOS',
        onChange: (value) => onDataChange(value, dataAndroid),
        validate: (value) => validateData(value, dataAndroid)
      })
      .addFormikInput({
        label: i18n.t('outdoorTenMaxBeaconMessageField.labels.dataAndroid'),
        name: 'tenMaxBeaconMessage.dataAndroid',
        onChange: (value) => onDataChange(value, dataIOS),
        validate: (value) => validateData(value, dataIOS)
      })
      .addFormikInput({
        label: i18n.t('outdoorTenMaxBeaconMessageField.labels.title'),
        name: 'tenMaxBeaconMessage.title',
        validate: validateTitle,
        hint: i18n.t('outdoorTenMaxBeaconMessageField.hints.inputLength', {
          length: 50
        })
      })
      .addFormikTextarea({
        label: i18n.t('outdoorTenMaxBeaconMessageField.labels.message'),
        name: 'tenMaxBeaconMessage.message',
        className: styles.linePushMessage,
        validate: validateMessage,
        hint: i18n.t('outdoorTenMaxBeaconMessageField.hints.inputLength', {
          length: 150
        })
      })
      .build();
  }, [
    beaconType,
    dataIOS,
    dataAndroid,
    onDataChange,
    validateBundleIds,
    validateData,
    validateTitle,
    validateMessage
  ]);

  return fields;
};
